import { FC, useMemo, useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "capitalrx-components-2.0/lib/forms";
import { Passwordbox, Textbox } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { AddNativeRegistrationRequest, addNativeRegistration, cognitoRegisterUser } from "@/App/api/login";
import { ContentPanel, PageWrapper, ThemedSubmitButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import { AuthenticationContext } from "../../context";


interface RegistrationForm {
  readonly email: string;
  readonly password: string;
  readonly confirmPassword: string;
}

export const AddNativeAuthenticationPage: FC<string> = () => {
  const { t: translate } = useTranslation();
  const defaultFormValues: RegistrationForm = useMemo(() => {
    return {
      email: "",
      password: "",
      confirmPassword: "",
    };
  }, []);
  const { getAuthToken } = useContext(AuthenticationContext);
  const { isViewingDataholder } = useContext(MemberEligibilityContext);
  const isEmulating = sessionStorage.getItem("isEmulation") === "true";
  const alert = useAlert();

  const form = useForm<RegistrationForm>({
    defaultValues: defaultFormValues,
  });
  const navigate = useNavigateClient();

  const validatePasswordMatch = () => {
    const password = form.watch("password");
    const confirmPassword = form.watch("confirmPassword");
    if (password !== confirmPassword) {
      return translate("ADD_NATIVE_AUTHENTICATION_PAGE.validatePasswordMatch");
    } else {
      return undefined;
    }
  };

  const submitForm = async (formData: RegistrationForm) => {
    await confirmRegisterUser(formData);
  };

  const confirmRegisterUser = async (formData: RegistrationForm) => {
    const body: AddNativeRegistrationRequest = {
      email: formData.email,
    };

    try {
      const registerUserResponse = await addNativeRegistration(await getAuthToken(), body);
      await cognitoRegisterUser({
        email: formData.email,
        password: formData.password,
        member: { ...registerUserResponse.member },
        plan: { ...registerUserResponse.plan },
      });
      navigate("/notifications?viewTab=account-preferences");
      logEvent(ANALYTIC_EVENTS.ACCOUNT.create);
    } catch (error) {
      alert({
        message: error?.data?.message || translate("ADD_NATIVE_AUTHENTICATION_PAGE.confirmRegisterUserErrorMessage"),
        type: "error",
        style: "toast",
      });
      logEvent(ANALYTIC_EVENTS.ERRORS.registerMember);
    }
  };

  return (
    <PageWrapper>
      <ContentPanel title={translate("ADD_NATIVE_AUTHENTICATION_PAGE.contentPanel")}>
        <Form form={form} disabled={isViewingDataholder || isEmulating} onSubmit={submitForm} testId="register">
          <Stack>
            <Grid>
              <Grid.Span columns={12}>
                <Subheading textAlign="center">{translate("ADD_NATIVE_AUTHENTICATION_PAGE.enterEmailAndPassword")}</Subheading>
              </Grid.Span>

              <Grid.Span columns={12}>
                <Textbox.Field
                  testId="email"
                  placeholder={translate("ADD_NATIVE_AUTHENTICATION_PAGE.email")}
                  name="email"
                  required
                  label={translate("ADD_NATIVE_AUTHENTICATION_PAGE.email")}
                />
              </Grid.Span>
              <Grid.Span columns={12}>
                <Passwordbox.Field
                  testId="password"
                  placeholder={translate("ADD_NATIVE_AUTHENTICATION_PAGE.password")}
                  name="password"
                  required
                  label={translate("ADD_NATIVE_AUTHENTICATION_PAGE.password")}
                />
              </Grid.Span>
              <Grid.Span columns={12}>
                <Passwordbox.Field
                  testId="confirm-password"
                  validator={validatePasswordMatch}
                  placeholder={translate("ADD_NATIVE_AUTHENTICATION_PAGE.confirmPassword")}
                  name="confirmPassword"
                  required
                  label={translate("ADD_NATIVE_AUTHENTICATION_PAGE.confirmPassword")}
                />
              </Grid.Span>

              <ThemedSubmitButton testId="create-account" appearance="primary" icon="arrow-right" iconPosition="right">
                {translate("ADD_NATIVE_AUTHENTICATION_PAGE.createAccount")}
              </ThemedSubmitButton>
            </Grid>
          </Stack>
        </Form>
      </ContentPanel>
    </PageWrapper>
  );
};
